import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { window } from "mixpanel-browser/src/window";

import { useJobCreation } from "./JobCreationContext";
import JobButtons from "./Job-Buttons";
import CreateJobLoadingOverlay from "./CreateJobLoadingOverlay";

import { Form, Select, message, InputNumber, Input } from "antd";
import "./Styles/Compensation.css";

const { Option } = Select;

export default function Compensation() {
  const [pageLoading, setPageLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { jobEdit, jobData: passedJobData } = location.state || {};
  const { jobData, updateJobData } = useJobCreation();

  useEffect(() => {
    const timeout = setTimeout(() => setPageLoading(false), 1000);
    return () => clearTimeout(timeout);
  }, []);
  // eslint-disable-next-line no-unused-vars
  const isEmpty = (value) => {
    return (
      value === undefined ||
      value === null ||
      value === "" ||
      (typeof value === "string" && value.trim().toUpperCase() === "N/A")
    );
  };

  const toNumberOrNA = (val, isEdit) => {
    if (val === undefined || val === null || val === "")
      return isEdit ? "N/A" : null;
    const num = Number(val);
    return Number.isFinite(num) ? num : isEdit ? "N/A" : null;
  };

  const toValueOrNA = (val, isEdit) => {
    if (!val) return isEdit ? "N/A" : null;
    return val;
  };

  useEffect(() => {
    const comp =
      jobEdit && passedJobData
        ? passedJobData.compensation || {}
        : jobData.compensation || {};

    const cleanComp = {
      minimum: toNumberOrNA(comp.minimum, jobEdit),
      maximum: toNumberOrNA(comp.maximum, jobEdit),
      currency: toValueOrNA(comp.currency, jobEdit),
      salary_date: toValueOrNA(comp.salary_date, jobEdit),
    };

    if (jobEdit && passedJobData) {
      updateJobData("id", passedJobData.id);
      updateJobData("compensation", cleanComp);
    }

    if (!form.isFieldsTouched()) {
      form.setFieldsValue(cleanComp);
    }
  }, [jobEdit, passedJobData, jobData, form, updateJobData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // helper to force all N/A
  const setAllNA = (setFieldsValue) => {
    setFieldsValue({
      minimum: "N/A",
      maximum: "N/A",
      currency: "N/A",
      salary_date: "N/A",
    });
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      const raw = form.getFieldsValue(true);

      // Validation for create/edit consistency
      const allValues = [
        raw.minimum,
        raw.maximum,
        raw.currency,
        raw.salary_date,
      ];
      const isAllEmpty = allValues.every(
        (v) => v === undefined || v === null || v === "" || v === "N/A"
      );
      const isAllFilled = allValues.every(
        (v) => v !== undefined && v !== null && v !== "" && v !== "N/A"
      );

      if (!jobEdit) {
        // Create mode
        if (!(isAllEmpty || isAllFilled)) {
          message.error(
            "Please either fill all salary fields or leave all empty."
          );
          return;
        }
      } else {
        // Edit mode
        const hasNA = allValues.some((v) => v === "N/A");
        if (hasNA && !allValues.every((v) => v === "N/A")) {
          message.error(
            "If 'N/A' is selected, all salary fields must be 'N/A'."
          );
          return;
        }
      }

      // Min/max numeric check
      if (
        raw.minimum &&
        raw.maximum &&
        raw.minimum !== "N/A" &&
        raw.maximum !== "N/A"
      ) {
        const minNum = Number(raw.minimum);
        const maxNum = Number(raw.maximum);
        if (!isNaN(minNum) && !isNaN(maxNum) && maxNum < minNum) {
          message.error("Maximum salary cannot be less than minimum salary.");
          return;
        }
      }

      const payload = {};
      if (
        raw.minimum != null &&
        raw.minimum !== "" &&
        (!jobEdit || raw.minimum !== "N/A")
      ) {
        payload.minimum = raw.minimum;
      } else if (jobEdit && raw.minimum === "N/A") {
        payload.minimum = "N/A";
      }

      if (
        raw.maximum != null &&
        raw.maximum !== "" &&
        (!jobEdit || raw.maximum !== "N/A")
      ) {
        payload.maximum = raw.maximum;
      } else if (jobEdit && raw.maximum === "N/A") {
        payload.maximum = "N/A";
      }

      if (raw.currency && (!jobEdit || raw.currency !== "N/A")) {
        payload.currency = raw.currency;
      } else if (jobEdit && raw.currency === "N/A") {
        payload.currency = "N/A";
      }

      if (raw.salary_date && (!jobEdit || raw.salary_date !== "N/A")) {
        payload.salary_date = raw.salary_date;
      } else if (jobEdit && raw.salary_date === "N/A") {
        payload.salary_date = "N/A";
      }

      setButtonLoading(true);
      updateJobData("compensation", payload);

      setTimeout(() => {
        navigate("/jobs/job-admin/job-posts/create-job/description", {
          state: { jobEdit },
        });
      }, 500);
    } catch {
      setButtonLoading(false);
      message.error("Please fix the errors in the form.");
    }
  };

  if (pageLoading) {
    return (
      <CreateJobLoadingOverlay
        image="https://res.cloudinary.com/diomlp95m/image/upload/v1752735823/Youthplus_Logoo_pjfjku.jpg"
        message="Loading Job Compensation..."
      />
    );
  }

  if (buttonLoading) {
    return (
      <CreateJobLoadingOverlay
        image="https://res.cloudinary.com/diomlp95m/image/upload/v1752735823/Youthplus_Logoo_pjfjku.jpg"
        message="Saving Compensation Details..."
      />
    );
  }

  return (
    <>
      <div className="job-compensate-head">
        <h2 className="job-compensate-title">Compensation</h2>
      </div>
      <div className="job-create-container">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleNext}
          className="job-create-form"
        >
          <div className="job-amount-container">
            {/* Minimum Amount */}
            <Form.Item label="Minimum Amount" shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => {
                const minValue = getFieldValue("minimum");
                const maxValue = getFieldValue("maximum");
                const isMinNA = jobEdit && minValue === "N/A";
                // eslint-disable-next-line no-unused-vars
                const isMaxNA = jobEdit && maxValue === "N/A";

                return (
                  <Input.Group compact style={{ width: "100%" }}>
                    <InputNumber
                      style={{ width: "70%" }}
                      min={0}
                      precision={0}
                      placeholder="Enter minimum amount"
                      disabled={isMinNA}
                      value={isMinNA ? null : minValue}
                      onChange={(val) => setFieldsValue({ minimum: val })}
                      formatter={(value) =>
                        `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) =>
                        value ? value.replace(/₦\s?|(,*)/g, "") : ""
                      }
                    />
                    {jobEdit && (
                      <Select
                        style={{ width: "30%" }}
                        value={isMinNA ? "N/A" : "Number"}
                        onChange={(val) => {
                          if (val === "N/A") setAllNA(setFieldsValue);
                          else
                            setFieldsValue({
                              minimum: null,
                              maximum: null,
                              currency: null,
                              salary_date: null,
                            });
                        }}
                      >
                        <Option value="Number">Number</Option>
                        <Option value="N/A">N/A</Option>
                      </Select>
                    )}
                  </Input.Group>
                );
              }}
            </Form.Item>

            {/* Maximum Amount */}
            <Form.Item shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => {
                const minValue = getFieldValue("minimum");
                const maxValue = getFieldValue("maximum");
                const isMinNA = jobEdit && minValue === "N/A";
                const isMaxNA = jobEdit && maxValue === "N/A";

                return (
                  <Form.Item
                    label="Maximum Amount"
                    name="maximum"
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const minVal = getFieldValue("minimum");
                          if (
                            (minVal === null ||
                              minVal === "" ||
                              minVal === "N/A") &&
                            value != null &&
                            value !== "N/A"
                          ) {
                            return Promise.reject(
                              new Error("Please enter a Minimum Amount first")
                            );
                          }
                          if (
                            minVal != null &&
                            minVal !== "" &&
                            minVal !== "N/A" &&
                            value != null &&
                            value !== "N/A" &&
                            Number(value) < Number(minVal)
                          ) {
                            return Promise.reject(
                              new Error("Maximum must be greater than Minimum")
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input.Group compact style={{ width: "100%" }}>
                      <InputNumber
                        style={{ width: "70%" }}
                        min={0}
                        precision={0}
                        placeholder="Enter maximum amount"
                        disabled={
                          isMaxNA ||
                          minValue === null ||
                          minValue === "" ||
                          minValue === "N/A" ||
                          isMinNA
                        }
                        value={isMaxNA ? null : maxValue}
                        onChange={(val) => {
                          setFieldsValue({ maximum: val });
                          if (minValue === "N/A")
                            setFieldsValue({ minimum: null });
                        }}
                        formatter={(value) =>
                          `${value ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }
                        parser={(value) =>
                          value ? value.replace(/₦\s?|(,*)/g, "") : ""
                        }
                      />
                      {jobEdit && (
                        <Select
                          style={{ width: "30%" }}
                          value={isMaxNA ? "N/A" : "Number"}
                          onChange={(val) => {
                            if (val === "N/A") setAllNA(setFieldsValue);
                            else
                              setFieldsValue({
                                minimum: null,
                                maximum: null,
                                currency: null,
                                salary_date: null,
                              });
                          }}
                        >
                          <Option value="Number">Number</Option>
                          <Option value="N/A">N/A</Option>
                        </Select>
                      )}
                    </Input.Group>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </div>

          {/* Currency */}
          <Form.Item label="Currency" name="currency">
            <Select
              placeholder="Select currency"
              allowClear
              onChange={(val) => {
                if (jobEdit && val === "N/A") {
                  form.setFieldsValue({
                    minimum: "N/A",
                    maximum: "N/A",
                    currency: "N/A",
                    salary_date: "N/A",
                  });
                }
              }}
            >
              <Option value="1">€ (Euro)</Option>
              <Option value="2">¥ (Yen)</Option>
              <Option value="3">₺ (Turkish Lira)</Option>
              <Option value="4">£ (Pound Sterling)</Option>
              <Option value="5">₽ (Russian Ruble)</Option>
              <Option value="6">zł (Polish Zloty)</Option>
              <Option value="7">₪ (Israeli Shekel)</Option>
              <Option value="8">R$ (Brazilian Real)</Option>
              <Option value="9">₹ (Indian Rupee)</Option>
              <Option value="10">₦ (Nigerian Naira)</Option>
              <Option value="11">$ (US Dollar)</Option>
              {jobEdit && <Option value="N/A">N/A</Option>}
            </Select>
          </Form.Item>

          {/* Salary Date */}
          <Form.Item label="Pay Frequency" name="salary_date">
            <Select
              placeholder="Select pay frequency"
              allowClear
              onChange={(val) => {
                if (jobEdit && val === "N/A") {
                  form.setFieldsValue({
                    minimum: "N/A",
                    maximum: "N/A",
                    currency: "N/A",
                    salary_date: "N/A",
                  });
                }
              }}
            >
              <Option value="per_hour">Per Hour</Option>
              <Option value="per_month">Per Month</Option>
              <Option value="per_year">Per Year</Option>
              {jobEdit && <Option value="N/A">N/A</Option>}
            </Select>
          </Form.Item>

          <JobButtons
            showPrevious
            continueText="Next"
            onContinue={handleNext}
          />
        </Form>
      </div>
    </>
  );
}
